import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import { shipmentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import ShipmentDetailModal from '../components/ShipmentDetailModal';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [shipmentSearch, setShipmentSearch] = useState('');
  const [searchedShipment, setSearchedShipment] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      const [statsRes, sessionsRes, healthRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getSessions(),
        adminAPI.getSystemHealth(),
      ]);

      setStats(statsRes.data);
      setSessions(sessionsRes.data.sessions || []);
      setHealth(healthRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      const errorMsg =
        err.response?.data?.message || err.message || 'Failed to load dashboard data';
      setError(`Error: ${errorMsg}. Please check that the backend server is running.`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';

    // Parse date - handle both ISO format and MySQL datetime format
    const date = new Date(dateString);

    // If invalid date, return fallback
    if (isNaN(date.getTime())) return 'Unknown';

    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 0) return 'Just now';
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  const getMonthDateRange = () => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[now.getMonth()];
    const day = now.getDate();
    return `${month} 1-${day}`;
  };

  const getCurrentDate = () => {
    const now = new Date();
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return {
      month: monthNames[now.getMonth()],
      day: now.getDate(),
      year: now.getFullYear()
    };
  };

  const handleViewShipment = async (shipment) => {
    try {
      const response = await shipmentsAPI.getById(shipment.id);
      const fullShipment = response.data.shipment;
      setSelectedShipment(fullShipment);
      setShowDetailModal(true);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load shipment details' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleDeleteShipment = async (shipmentId) => {
    if (!window.confirm('Are you sure you want to delete this shipment? This action cannot be undone.')) {
      return;
    }

    try {
      await shipmentsAPI.delete(shipmentId);
      setMessage({ type: 'success', text: 'Shipment deleted successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      // Refresh dashboard data
      fetchDashboardData();
      setShowDetailModal(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete shipment' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedShipment(null);
  };

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'super_user';

  const handleSearchShipment = async (e) => {
    e.preventDefault();
    
    if (!shipmentSearch || shipmentSearch.trim() === '') {
      setMessage({ type: 'error', text: 'Please enter a shipment ID' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setSearchLoading(true);
    setSearchedShipment(null);

    try {
      const response = await shipmentsAPI.getById(parseInt(shipmentSearch));
      setSearchedShipment(response.data.shipment);
      setMessage({ type: 'success', text: `Found Shipment #${shipmentSearch}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: `Shipment #${shipmentSearch} not found` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleResendNotification = async (shipmentId) => {
    if (!window.confirm(`Resend email notification for Shipment #${shipmentId}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/shipments/${shipmentId}/resend-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: `✓ Notification resent for Shipment #${shipmentId}` });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to resend notification' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }

    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  if (loading) {
    return (
      <div className="app-container">
        <Header />
        <Navigation activeTab="admin" />
        <div className="content">
          <div className="loading">Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="admin" />

      <div className="content">
        <div className="admin-header">
          <h1 className="page-title">Admin Dashboard</h1>
          <div className="admin-actions">
            <button className="btn-secondary" onClick={() => navigate('/admin/users')}>
              Manage Users
            </button>
            <button className="btn-secondary" onClick={() => navigate('/admin/shipments')}>
              All Shipments
            </button>
            <button className="btn-secondary" onClick={() => navigate('/admin/reports')}>
              Reports
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <div className="stat-value">{stats?.total_shipments || 0}</div>
              <div className="stat-label">Total Shipments</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-value">{stats?.total_users || 0}</div>
              <div className="stat-label">Total Users</div>
            </div>
          </div>

          <div className="stat-card stat-card-clickable" onClick={() => document.getElementById('active-sessions-section')?.scrollIntoView({ behavior: 'smooth' })}>
            <div className="stat-icon">🟢</div>
            <div className="stat-content">
              <div className="stat-value">{stats?.active_sessions || 0}</div>
              <div className="stat-label">Users Online</div>
              {sessions.length > 0 && (
                <div className="stat-preview">
                  {sessions.slice(0, 3).map((s, i) => (
                    <span key={i} className="online-user-badge">{s.name}</span>
                  ))}
                  {sessions.length > 3 && <span className="more-users">+{sessions.length - 3} more</span>}
                </div>
              )}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-date">
              <div className="date-month">{getCurrentDate().month}</div>
              <div className="date-day">{getCurrentDate().day}</div>
              <div className="date-year">{getCurrentDate().year}</div>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.shipments_this_month || 0}</div>
              <div className="stat-label">This Month ({getMonthDateRange()})</div>
            </div>
          </div>
        </div>

        {/* Shipments by Status */}
        <div className="dashboard-section">
          <h2 className="section-title">Shipments by Status</h2>
          <div className="status-cards-grid">
            {stats?.shipments_by_status &&
              Object.entries(stats.shipments_by_status).map(([status, count]) => (
                <div
                  key={status}
                  className={`status-card status-card-${status}`}
                  onClick={() => navigate(`/admin/shipments?status=${status}`)}
                  title={`Click to view all ${status} shipments`}
                >
                  <div className="status-card-count">{count}</div>
                  <div className="status-card-label">{status}</div>
                </div>
              ))}
          </div>
        </div>

        <div className="dashboard-row">
          {/* Active Sessions */}
          <div id="active-sessions-section" className="dashboard-section half">
            <h2 className="section-title">👥 Who's Online ({sessions.length})</h2>
            <div className="sessions-list">
              {sessions.length === 0 ? (
                <div className="empty-state">No active sessions</div>
              ) : (
                sessions.map((session) => (
                  <div key={session.id} className="session-item">
                    <div className="session-user">
                      <span className="session-name">{session.name}</span>
                      <span className="session-username">({session.username})</span>
                      {session.role === 'admin' && <span className="badge-admin">Admin</span>}
                    </div>
                    <div className="session-details">
                      <span className="session-time">{formatTimeAgo(session.last_activity)}</span>
                      <span className="session-ip">{session.ip_address}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* System Health */}
          <div className="dashboard-section half">
            <h2 className="section-title">System Health</h2>
            {health && (
              <div className="health-info">
                <div className="health-item">
                  <span className="health-label">Database Status</span>
                  <span className="health-value status-good">✓ {health.database?.status}</span>
                </div>
                <div className="health-item">
                  <span className="health-label">Database Size</span>
                  <span className="health-value">{health.database?.size_mb?.toFixed(2)} MB</span>
                </div>
                <div className="health-item">
                  <span className="health-label">Active Connections</span>
                  <span className="health-value">{health.database?.active_connections}</span>
                </div>
                <div className="health-item">
                  <span className="health-label">Upload Storage</span>
                  <span className="health-value">
                    {health.storage?.upload_size_mb?.toFixed(2)} MB ({health.storage?.file_count}{' '}
                    files)
                  </span>
                </div>
                <div className="health-item">
                  <span className="health-label">Last Backup</span>
                  <span className="health-value">
                    {health.backup?.last_backup
                      ? formatTimeAgo(health.backup.last_backup)
                      : 'Never'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Shipments */}
        <div className="dashboard-section">
          <div className="section-header-with-search">
            <h2 className="section-title">Recent Shipments</h2>
            
            {isSuperAdmin && (
              <form onSubmit={handleSearchShipment} className="shipment-search-form">
                <input
                  type="number"
                  value={shipmentSearch}
                  onChange={(e) => setShipmentSearch(e.target.value)}
                  placeholder="Search by Shipment ID..."
                  className="search-input"
                  min="1"
                />
                <button type="submit" className="btn-search" disabled={searchLoading}>
                  {searchLoading ? '🔍 Searching...' : '🔍 Search'}
                </button>
              </form>
            )}
          </div>
          
          {message.text && (
            <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
              {message.text}
            </div>
          )}

          {/* Searched Shipment Result */}
          {searchedShipment && (
            <div className="searched-shipment-result">
              <div className="result-header">
                <h3>Search Result: Shipment #{searchedShipment.id}</h3>
                <button className="btn-clear-search" onClick={() => setSearchedShipment(null)}>
                  Clear Search
                </button>
              </div>
              <table className="shipments-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Type</th>
                    <th>From → To</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>#{searchedShipment.id}</td>
                    <td>{searchedShipment.user_name || 'N/A'}</td>
                    <td>
                      <span className={`badge-type ${searchedShipment.shipment_type}`}>
                        {searchedShipment.shipment_type}
                      </span>
                    </td>
                    <td>
                      {searchedShipment.shipper_city}, {searchedShipment.shipper_state} →{' '}
                      {searchedShipment.consignee_city}, {searchedShipment.consignee_state}
                    </td>
                    <td>
                      <span className={`badge-status ${searchedShipment.status}`}>
                        {searchedShipment.status}
                      </span>
                    </td>
                    <td>{formatTimeAgo(searchedShipment.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="view-button" onClick={() => handleViewShipment(searchedShipment)}>
                          View
                        </button>
                        <button 
                          className="resend-button" 
                          onClick={() => handleResendNotification(searchedShipment.id)}
                        >
                          🔄 Resend
                        </button>
                        {isSuperAdmin && (
                          <button className="delete-button" onClick={() => handleDeleteShipment(searchedShipment.id)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          
          <div className="recent-shipments">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading shipments...</p>
              </div>
            ) : stats?.recent_shipments && stats.recent_shipments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <p className="empty-state-title">No recent shipments</p>
                  <p className="empty-state-text">
                    Shipments will appear here once users start creating them
                  </p>
                </div>
              </div>
            ) : (
              <table className="shipments-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Type</th>
                    <th>From → To</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recent_shipments?.map((shipment) => (
                    <tr key={shipment.id}>
                      <td>
                        #{shipment.id}
                        {shipment.attachment_count > 0 && (
                          <span title={`${shipment.attachment_count} attachment(s)`} style={{ marginLeft: '6px', opacity: 0.7 }}>📎</span>
                        )}
                      </td>
                      <td>{shipment.user_name}</td>
                      <td>
                        <span className={`badge-type ${shipment.shipment_type}`}>
                          {shipment.shipment_type}
                        </span>
                      </td>
                      <td>
                        {shipment.shipper_city}, {shipment.shipper_state} →{' '}
                        {shipment.consignee_city}, {shipment.consignee_state}
                      </td>
                      <td>
                        <span className={`badge-status ${shipment.status}`}>{shipment.status}</span>
                      </td>
                      <td>{formatTimeAgo(shipment.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="view-button" onClick={() => handleViewShipment(shipment)}>
                            View
                          </button>
                          {isSuperAdmin && (
                            <>
                              <button 
                                className="resend-button" 
                                onClick={() => handleResendNotification(shipment.id)}
                              >
                                🔄 Resend
                              </button>
                              <button className="delete-button" onClick={() => handleDeleteShipment(shipment.id)}>
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Shipment Detail Modal */}
      {showDetailModal && selectedShipment && (
        <ShipmentDetailModal
          shipment={selectedShipment}
          onClose={closeDetailModal}
          onClone={() => {}}
          showCloneButton={false}
          showDeleteButton={isSuperAdmin}
          onDelete={() => handleDeleteShipment(selectedShipment.id)}
          onReject={() => {
            fetchDashboardData();
            setMessage({ type: 'success', text: 'Shipment rejected successfully' });
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
          }}
        />
      )}
    </div>
  );
}

export default AdminDashboard;
